import './styles.scss';
import { CellMeasurer, CellMeasurerCache } from 'react-virtualized/dist/commonjs/CellMeasurer';
import React, { useRef, useEffect, useState } from 'react';
import List, { ListRowRenderer } from 'react-virtualized/dist/commonjs/List';
import { Button, Checkbox, Input, Radio } from '@myua/kit';
import { SearchNormal, TickSquare, Trash } from '@myua/icons/dist/linear16';

export const EnumTuner: React.FC<{
  searchText: string;
  searchResultItems: { id: any; title: string }[];
  isSingle: boolean;
  checkedItems: Record<string, boolean>;
  itemsOnChange: (ids: string[], v: boolean) => void;
  itemOnSwitch: (id: string) => void;
  applyOnClick: () => void;
  searchOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  closeOnClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}> = (props) => {
  const listRef = useRef<List>(null);
  // Кэш высот строк — свой у каждого тюнера. Раньше он был модульным и ключевался
  // по columnId, а все вызывающие передавали одно и то же 'xxx': скрытый поповер
  // соседней колонки (antd держит его смонтированным под display: none) при
  // ререндере перемерял строки с нулевой высотой, и в открытом фильтре первые
  // пункты наезжали друг на друга (MYUA-16744).
  const [cache] = useState(() => new CellMeasurerCache({ defaultWidth: 310, fixedWidth: true }));

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listRef.current?.forceUpdateGrid();
  });

  // Первый рендер antd Popover делает до того, как попап получил раскладку, —
  // CellMeasurer успевает измерить видимые строки (+ overscan) нулевой высотой
  // и кэширует ноль. Короткий список (Team, Mislead) из-за этого выглядел
  // пустым, длинный — с наслоением первых пунктов (MYUA-16744). Как только
  // тюнер реально получил размер, сбрасываем кэш и перемеряем строки.
  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    let wasVisible = false;
    const observer = new ResizeObserver(() => {
      const visible = node.offsetHeight > 0;
      if (visible && !wasVisible) {
        cache.clearAll();
        listRef.current?.recomputeRowHeights();
      }
      wasVisible = visible;
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [cache]);

  const filtered = props.searchText.trim() !== '';

  const noRowsRenderer = (): JSX.Element => {
    return <>{props.searchText.trim() === '' ? 'No data' : 'Nothing found'}</>;
  };

  const rowRenderer: ListRowRenderer = ({ index, key, style, parent }) => {
    const item = props.searchResultItems[index];
    const isChecked = props.checkedItems[item.id] ?? false;
    const className = `gen-filter-tuner__item ${isChecked ? 'gen-filter-tuner__item_checked' : ''}`;

    // Single-режим — переключатель «одно из», поэтому Radio; иначе обычный
    // Checkbox. Раньше это был хардкод `ui ${isSingle ? 'radio' : ''} checkbox`
    // с самодельной парой input + label.
    const Control = props.isSingle ? Radio : Checkbox;

    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
        <div className={className} style={style}>
          <Control
            className="gen-filter-tuner__control"
            checked={isChecked}
            onChange={(e) => {
              if (props.isSingle) {
                props.itemOnSwitch(item.id);
                props.applyOnClick();
              } else {
                props.itemsOnChange([item.id], e.target.checked);
              }
            }}
          >
            {item.title}
          </Control>
        </div>
      </CellMeasurer>
    );
  };

  cache.clearAll();

  const listWidth = cache.columnWidth({ index: 0 });

  const searchResultItemIds = props.searchResultItems.map(({ id }) => id);

  return (
    <div ref={rootRef} className="gen-filter-tuner gen-filter-tuner_enum">
      <Input
        autoFocus
        prefix={<SearchNormal />}
        value={props.searchText}
        onChange={props.searchOnChange}
        // Было onKeyPress с DOM-типом KeyboardEvent — событие deprecated
        // в React 17+, а тип не совпадал с реальным. Поведение то же.
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (
            e.key === 'Enter' &&
            props.searchText.trim() !== '' &&
            searchResultItemIds.length > 0 &&
            !props.isSingle
          ) {
            props.itemsOnChange(searchResultItemIds, true);
          }
        }}
        className="gen-filter-tuner__search"
        placeholder={'Search'}
      />

      <div className="gen-list gen-filter-tuner__list">
        <div className="gen-filter-tuner__head">
          <div className="gen-filter-tuner__head-buttons">
            {props.searchResultItems.length > 0 && !props.isSingle ? (
              <>
                <Button
                  size="small"
                  className="gen-filter-tuner__head-item"
                  icon={<TickSquare />}
                  onClick={() => props.itemsOnChange(searchResultItemIds, true)}
                >
                  {filtered ? 'Check filtered' : 'Check all'}
                </Button>
                <Button
                  size="small"
                  className="gen-filter-tuner__head-item"
                  icon={<Trash />}
                  onClick={() => props.itemsOnChange(searchResultItemIds, false)}
                >
                  {filtered ? 'Clear filtered' : 'Clear'}
                </Button>
              </>
            ) : null}
          </div>
        </div>

        <List
          className="gen-no-outline"
          ref={listRef}
          height={220}
          overscanRowCount={10}
          noRowsRenderer={noRowsRenderer}
          rowCount={props.searchResultItems.length}
          rowHeight={cache.rowHeight}
          rowRenderer={rowRenderer}
          width={listWidth}
          tabIndex={null}
        />
      </div>

      <div className="gen-filter-tuner__buttons">
        <Button type="primary" htmlType="button" onClick={props.applyOnClick}>
          Apply
        </Button>
        <Button htmlType="button" onClick={props.closeOnClick}>
          Close
        </Button>
      </div>
    </div>
  );
};
